import { useEffect, useState } from "react";
import { api } from "@/helper/api";
import type { IDocumentTemplateData } from "@/types/DocumentTemplateData";
import type {
  DocumentTemplateParameter,
  ParameterValues,
} from "@/types/DocumentTemplateParameter";
import { validateParameterValues } from "@/components/documentTemplate/parameterValues";

interface LoadedParameters {
  templateId: string;
  definitions: DocumentTemplateParameter[];
  values: ParameterValues;
}

export function useTemplateParameters(
  templateId: string | undefined,
  enabled: boolean
) {
  const [loaded, setLoaded] = useState<LoadedParameters | null>(null);
  const [failedId, setFailedId] = useState<string>();
  const [attempt, setAttempt] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let current = true;
    setLoaded(null);
    setFailedId(undefined);
    setErrors({});
    if (enabled && templateId) {
      api.genericApiRequest({
        entity: "documentTemplate",
        entityId: templateId,
        method: "get",
        successHandler: (response: { data: IDocumentTemplateData }) => {
          if (!current) return;
          const { parameters, parameter_defaults } = response.data;
          if (!Array.isArray(parameters) || !parameter_defaults) {
            setFailedId(templateId);
            return;
          }
          setLoaded({
            templateId,
            definitions: parameters,
            values: parameter_defaults,
          });
        },
        failHandler: () => {
          if (current) setFailedId(templateId);
        },
      });
    }
    return () => {
      current = false;
    };
  }, [templateId, enabled, attempt]);

  const ready = enabled && !!templateId && loaded?.templateId === templateId;
  return {
    ready,
    failed: !!templateId && failedId === templateId,
    loading: !!templateId && !ready && failedId !== templateId,
    definitions: ready ? loaded.definitions : [],
    values: ready ? loaded.values : {},
    errors,
    retry: () => setAttempt((value) => value + 1),
    onChange: (name: string, value: ParameterValues[string]) => {
      setLoaded((previous) =>
        previous && previous.templateId === templateId
          ? { ...previous, values: { ...previous.values, [name]: value } }
          : previous
      );
      setErrors((previous) => {
        const next = { ...previous };
        delete next[name];
        return next;
      });
    },
    validate: (): ParameterValues | null => {
      if (!ready) return null;
      const result = validateParameterValues(loaded.definitions, loaded.values);
      setErrors(result.errors);
      return Object.keys(result.errors).length ? null : result.values;
    },
  };
}
